import { z } from 'zod';
import { pageInfoSchema } from './pagination';

/**
 * Primitive request/response rules shared by every Phase 0 contract (PLAN §4 "Primitive rules").
 * Request schemas trim and NFC-normalise text; business rules that answer 422 (timezone, phone,
 * password policy, accent contrast) are checked by the API with `@orbit/shared/validators` and
 * `@orbit/shared/password-policy`, because a 400 would hide the specific error code.
 */

/**
 * A link that is stored and later shown or followed (evidence, source records, poll URLs):
 * http(s) only. `z.url()` alone accepts every scheme, `javascript:` and `data:` included, which
 * would be a stored XSS the day a screen renders the value as a link (D-389).
 */
export const httpUrlSchema = z.url({ protocol: /^https?$/ }).max(2000);

/** Control characters other than \n and \t. */
const SINGLE_LINE = /^[^\p{Cc}]*$/u;
const MULTI_LINE = /^(?:[^\p{Cc}]|[\n\t])*$/u;

/** Trimmed, NFC-normalised single-line text of `min`–`max` characters, no control characters. */
export function singleLineText(min: number, max: number) {
  return z
    .string()
    .trim()
    .normalize('NFC')
    .min(min)
    .max(max)
    .regex(SINGLE_LINE, 'Control characters are not allowed');
}

/** As {@link singleLineText} but allows line breaks and tabs. */
export function multiLineText(min: number, max: number) {
  return z
    .string()
    .trim()
    .normalize('NFC')
    .min(min)
    .max(max)
    .regex(MULTI_LINE, 'Control characters are not allowed');
}

/** UUID (the app generates UUIDv7, D-003). */
export const uuidSchema = z.uuid();

/** ISO-8601 instant in UTC with `Z`, e.g. `2026-09-28T04:30:00.000Z` (D-011). */
export const isoDateTimeSchema = z.iso.datetime();

/** Calendar date `YYYY-MM-DD`, read in the workspace timezone. */
export const localDateSchema = z.iso.date();

/** Request email: trimmed, lowercased, ≤ 254 chars (stored as citext, D-012). */
export const emailSchema = z.string().trim().toLowerCase().max(254).pipe(z.email());

/** Response email (already normalised by the server). */
export const emailOutSchema = z.string();

/**
 * Password as sent: 1–1024 characters. The policy (10–256 code points, not common) is checked
 * separately and answers `422 password_policy_violation` with every reason.
 */
export const passwordSchema = z.string().min(1).max(1024);

/** A person's name: trimmed, NFC, 1–100 characters. */
export const personNameSchema = singleLineText(1, 100);
/** Display name: as a person name but 1–80 characters. */
export const displayNameSchema = singleLineText(1, 80);
/** Job title: 1–100 characters. */
export const titleSchema = singleLineText(1, 100);

/** Phone as typed; the API parses it (default region IN) and answers `422 invalid_phone`. */
export const phoneInputSchema = z.string().trim().min(1).max(32);

/** IANA time zone name; the API answers `422 invalid_timezone` for unknown names. */
export const timeZoneInputSchema = z.string().trim().min(1).max(64);

/** 6-digit OTP or reset code. */
export const otpCodeSchema = z.string().regex(/^[0-9]{6}$/, 'Enter the 6-digit code');

/** 32 random bytes as base64url without padding (refresh and invite tokens). */
export const opaqueTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'Invalid token');

/** Native device name, e.g. "Akshay's iPhone". Web labels come from the User-Agent. */
export const deviceNameSchema = singleLineText(1, 100);

/** `#RRGGBB`. */
export const hexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Use #RRGGBB');

/** Query-string boolean: `true`/`false`/`1`/`0`. */
export const queryBooleanSchema = z
  .enum(['true', 'false', '1', '0'])
  .transform((v) => v === 'true' || v === '1');

/** Query-string comma list (`?role=admin,manager`), trimmed, empty items dropped. */
export function commaList<T extends z.ZodType<unknown, string>>(
  item: T,
  options: { max?: number } = {},
) {
  const array = z.array(item).min(1);
  return z
    .string()
    .transform((value) =>
      value
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean),
    )
    .pipe(options.max ? array.max(options.max) : array);
}

/** Request bodies that take no fields (`{}`), e.g. action endpoints. */
export const emptyBodySchema = z.strictObject({});
export type EmptyBody = z.infer<typeof emptyBodySchema>;

/**
 * `{ items, nextCursor: null, page }` for small bounded lists that return everything.
 *
 * It carries the page block like every other list (D-1258) even though it never has a second
 * page: a client drawing pagination controls should not have to know which kind of list it is
 * reading, and "1 of 1, nothing either side" is the honest answer rather than a missing field.
 */
export function boundedList<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.null(), page: pageInfoSchema.optional() });
}
